import { useMemo } from 'react';

import {
  ORDENABLE_PAGE_SIZE,
  REQUEST_STALE_TIME_IN_MS as staleTime,
} from '@/constants';
import useListService from '@/data/hook/useListService';
import { scheduledLessonExamQueries } from '@/data/query-keys/scheduledLessonExamQueryKey';
import { studentExamQueries } from '@/data/query-keys/studentExamQueryKey';
import { KlassStatusEnum } from '@/models/Klass';
import { toStudentExamRecords } from '@/utils/student-exam/studentExamRecordAdapters';
import { datetimeSort } from '@/utils/shared/datetimeSort';

export type UseStudentExamRecordsParams = {
  studentId: number;
  klassId?: number;
};

export default function useStudentExamRecords({
  studentId,
  klassId,
}: UseStudentExamRecordsParams) {
  const enabled = studentId > 0 && !!klassId;

  const {
    results: studentExams,
    isInitialLoading: isLoadingStudentExams,
    error: studentExamsError,
  } = useListService({
    ...studentExamQueries.list({
      studentId: [studentId],
      ordering: '-finished_at',
      klassId: klassId ? [klassId] : undefined,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled,
    keepPreviousData: true,
    staleTime,
  });

  const {
    results: scheduledLessonExams,
    isInitialLoading: isLoadingScheduledLessonExams,
    error: scheduledLessonExamsError,
  } = useListService({
    ...scheduledLessonExamQueries.list({
      klassId,
      klassStatus: [KlassStatusEnum.OPEN, KlassStatusEnum.IN_PROGRESS],
      isActive: true,
      ordering: '-datetime',
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled,
    keepPreviousData: true,
    staleTime,
  });

  const records = useMemo(
    () =>
      toStudentExamRecords({
        studentExams,
        scheduledLessonExams,
        klassId: klassId ?? 0,
      }).sort(datetimeSort),
    [studentExams, scheduledLessonExams, klassId],
  );

  return {
    records,
    isLoading: isLoadingStudentExams || isLoadingScheduledLessonExams,
    error: studentExamsError ?? scheduledLessonExamsError,
  };
}
